import React from 'react'
import { Link, useLocation } from 'react-router-dom';

import {
    Row,
    Col5,
    Col7,
    TitleContainer,
    TitleXs,
    TitleLg,
    TitleXl,
    BackArrow,
    BodySm,
    WideScreenContainer,
    TitleSm,
} from 'components/elements'

import {
    FilterCard,
    JobCategoryPageWrap,
    CategoryFilterSection,
    CategorySectionRight,
    SearchGroup,
    SearchBtn,
    SearchInput,
    SearchBox,
    BadgesRow,
    ResetButton,
    BadgeContainer,
    HeaderContainer,
    ViewAllContainer
} from './elements'

import ViewAll from 'components/ViewAll'
import JobPostCard from 'components/JobPostCard'

import { DynamicContent, r3cPageContent } from 'constants/index'
import LoadingHoc from "../../hoc/LoadingHOC";
import BackArrowSvg from "../../assets/icons/BackArrowSvg";
import FooterAnimation from "../../components/FooterAnimation";
import CollapsibleCheckboxArea from "../../components/CollapsibleCheckboxArea";

/** 
 * Normalizes a location name so that "City, Country" is consistently
 * "ShorterName, LongerName" for city-level comparisons. 
 * Leaves "Remote - X" style strings alone.
 */
/**
 * Normalizes a location name consistently for filtering and display.
 * - Handles "City, Country" or "City, State" order based on length.
 * - Specifically collapses "Remote - USA" to "Remote".
 * - Handles plain "Remote" case.
 * - Returns other specific "Remote - X" variations (like "Remote - UK", "Remote - Canada") as is.
 * - Returns other formats (e.g., single names) as is after trimming.
 * - Returns null for invalid input (null, undefined, non-string, empty).
 */
const normalizeLocationName = (name) => {
    // 1. Input Validation & Trimming
    if (!name || typeof name !== 'string') {
        return null;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
        return null;
    }

    // Use lowercase for case-insensitive comparisons
    const lowerTrimmedName = trimmedName.toLowerCase();

    // 2. Handle Remote Variations
    if (lowerTrimmedName.startsWith("remote")) {
        if (lowerTrimmedName === "remote - usa" || lowerTrimmedName === "remote") {
            return "Remote";
        }
        return trimmedName;
    }

    // 3. Special case for Switzerland/Zurich
    if (lowerTrimmedName === "switzerland" || lowerTrimmedName === "zurich, switzerland" || lowerTrimmedName === "switzerland, zurich") {
        return "Zurich, Switzerland";
    }

    // 4. Special case for Toronto - always return "Remote - Canada"
    if (lowerTrimmedName === "toronto" ||
        lowerTrimmedName === "toronto, ontario" ||
        lowerTrimmedName === "canada, toronto") {
        return "Remote - Canada";
    }

    // 5. Handle "City, State" or "City, Country" combinations
    const parts = trimmedName.split(",").map(part => part.trim()).filter(Boolean);

    if (parts.length === 2) {
        const [part1, part2] = parts;
        // If one part is a 2-letter state code, put it second
        if (/^[A-Za-z]{2}$/.test(part1)) {
            return `${part2}, ${part1.toUpperCase()}`;
        }
        if (/^[A-Za-z]{2}$/.test(part2)) {
            return `${part1}, ${part2.toUpperCase()}`;
        }
        // For other combinations, keep original order but ensure proper capitalization
        return `${part1}, ${part2}`;
    }

    // 6. Fallback - return trimmed name with proper capitalization
    return trimmedName.split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
};


function JobCategoryPage(props) {
    const { setLoading } = props;
    const { pathname } = useLocation();
    const a = pathname.split('/');
    let initialFilter = null;
    if (a && a.length > 2 && a[2].length > 0) {
        initialFilter = a[2];
    }

    const [capabilities, selectCapabilities] = React.useState(["All Careers"]);
    const [locations, selectLocations] = React.useState(["All Locations"]);
    const [allLocations, setAllLocationOptions] = React.useState({});
    const [allCapabilities, setAllCapabilityOptions] = React.useState({});
    const [allJobs, setAllJobs] = React.useState([]);
    const [currentJobs, setCurrentJobs] = React.useState([]);

    const [scrollTop, setScrollTop] = React.useState(false);
    const [scrollThresh, setScrollThresh] = React.useState(0);
    const [threshold, setThreshold] = React.useState(0);

    // For search prototype
    const [searchedJob, setSearchedJob] = React.useState('');
    const [totalJobs, setTotalJobs] = React.useState([]);

    const locationRef = React.useRef(null);
    const filterRef = React.useRef(null);

    /************************************************
     * SCROLL LISTENER
     ***********************************************/
    React.useEffect(() => {
        // We set threshold for applying the "sticky" style
        // to the filter card after scrolling past a certain point
        setThreshold(locationRef.current.offsetTop);

        const handleScroll = () => {
            setScrollTop(scrollThresh < window.scrollY);
            setScrollThresh(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [threshold, scrollThresh, scrollTop]);

    /************************************************
     * UTILS TO UPDATE STATE
     ***********************************************/
    const updateLocationHelper = (option, allLocsRef, locsRef) => {
        const allLocs = JSON.parse(JSON.stringify(allLocsRef));
        let locs = [...locsRef];

        // If we only have "All Locations", remove that first before adding anything
        if (locs.includes("All Locations") && locs.length < 2) {
            locs = [];
            allLocs["All Locations"].checked = false;
        }

        // If user selects "All Locations" from scratch
        if (option === "All Locations") {
            locs = [];
            Object.keys(allLocs).forEach((country) => {
                if (allLocs[country]) {
                    allLocs[country].checked = false;
                    if (allLocs[country].cities) {
                        allLocs[country].cities.forEach((city) => {
                            city.checked = false;
                        });
                    }
                }
            });
        }

        // Check if it is a city or a country
        if (allLocs[option]?.cities) {
            // option is a country
            if (option === "All Locations") {
                if (allLocs[option].checked) {
                    const idx = locs.indexOf("All Locations");
                    if (idx !== -1) {
                        locs.splice(idx, 1);
                    }
                } else {
                    locs.push("All Locations");
                }
            } else {
                // toggling a country (all its cities)
                if (allLocs[option].checked) {
                    // uncheck all its cities
                    allLocs[option].cities.forEach((city) => {
                        city.checked = false;
                        const cityIdx = locs.indexOf(city.name);
                        if (cityIdx !== -1) {
                            locs.splice(cityIdx, 1);
                        }
                    });
                } else {
                    // check all its cities
                    allLocs[option].cities.forEach((city) => {
                        city.checked = true;
                        locs.push(city.name);
                    });
                }
            }
            allLocs[option].checked = !allLocs[option].checked;
        } else {
            // This is an actual city object
            if (option && option.country && allLocs[option.country]) {
                allLocs[option.country].checked = true;
                allLocs[option.country].cities = allLocs[option.country].cities.map((element) => {
                    if (element.name === option.name) {
                        element.checked = !element.checked;
                    }
                    return element;
                });

                // If all cities in a country are unchecked, uncheck the country
                if (allLocs[option.country].cities.every((obj) => obj.checked === false)) {
                    allLocs[option.country].checked = false;
                }

                if (!option.checked) {
                    locs.push(option.name);
                } else {
                    const index = locs.indexOf(option.name);
                    if (index !== -1) {
                        locs.splice(index, 1);
                    }
                }
            }
        }

        // If there are no locations selected, fallback to "All Locations"
        if (locs.length === 0) {
            locs = ["All Locations"];
            allLocs["All Locations"].checked = true;
        }
        return [allLocs, locs];
    };

    const updateCapabilityHelper = (option, allCapsRef, capsRef) => {
        const allCaps = JSON.parse(JSON.stringify(allCapsRef));
        let caps = [...capsRef];

        // If user chooses "All Careers", reset everything
        if (option === "All Careers") {
            caps = ["All Careers"];
            Object.keys(allCaps).forEach((i) => {
                allCaps[i].checked = false;
            });
            allCaps["All Careers"].checked = true;
            return [allCaps, caps];
        }

        // If we currently only have "All Careers", remove that first
        if (caps[0] === "All Careers" && caps.length === 1) {
            caps = [option];
            allCaps["All Careers"].checked = false;
        } else {
            // Toggle
            const index = caps.indexOf(option);
            if (index !== -1) {
                caps.splice(index, 1);
            } else {
                caps.push(option);
            }
        }

        // If user unselects everything, revert to "All Careers"
        if (caps.length === 0) {
            caps = ["All Careers"];
            allCaps["All Careers"].checked = true;
        }

        // Update the checkbox state
        allCaps[option].checked = !allCaps[option].checked;

        // If selecting a specific capability, ensure "All Careers" is unchecked
        if (option !== "All Careers" && allCaps[option].checked) {
            allCaps["All Careers"].checked = false;
        }

        return [allCaps, caps];
    };

    /************************************************
     * CLEAR FILTERS
     ***********************************************/
    const clearFilters = () => {
        // Resets to "All Careers" + "All Locations"
        selectCapabilities(["All Careers"]);
        selectLocations(["All Locations"]);

        // Reset location checkboxes
        const resetLocs = JSON.parse(JSON.stringify(allLocations));
        Object.keys(resetLocs).forEach((loc) => {
            resetLocs[loc].checked = false;
            if (resetLocs[loc].cities) {
                resetLocs[loc].cities.forEach((city) => {
                    city.checked = false;
                });
            }
        });
        resetLocs["All Locations"].checked = true;
        setAllLocationOptions(resetLocs);

        // Reset capability checkboxes
        const resetCaps = JSON.parse(JSON.stringify(allCapabilities));
        Object.keys(resetCaps).forEach((cap) => {
            resetCaps[cap].checked = false;
        });
        resetCaps["All Careers"].checked = true;
        setAllCapabilityOptions(resetCaps);
    };

    /************************************************
     * PREP LOCATION DATA: CITY + COUNTRY DE-DUPLICATION
     ***********************************************/
    const normalizeLocations = (locations) => {
        // Initialize allLocs with "All Locations" first
        const allLocs = {
            "All Locations": {
                cities: [],
                checked: false,
                count: 0
            }
        };

        // Initialize Europe object
        allLocs["Europe"] = {
            cities: [],
            checked: false,
            count: 0
        };

        // We'll store sets of jobIndexes for each country
        const countryToJobsMap = {};

        // Define European countries
        const europeanCountries = [
            "United Kingdom", "France", "Germany", "Italy", "Spain",
            "Netherlands", "Belgium", "Switzerland", "Austria", "Sweden",
            "Norway", "Denmark", "Finland", "Portugal", "Greece",
            "Ireland", "Poland", "Czech Republic", "Hungary", "Romania",
            "Bulgaria", "Croatia", "Slovakia", "Slovenia", "Luxembourg",
            "Estonia", "Latvia", "Lithuania", "Cyprus", "Malta"
        ];

        // Build out city arrays
        for (let i = 0; i < locations.length; i++) {
            let country = locations[i].country;
            let city = locations[i].city;
            let name = locations[i].name; // This is the original name from locations[i]
            const state = locations[i].state;

            // Skip Toronto locations - they'll be handled as Remote - Canada
            if (name.toLowerCase().includes('toronto') ||
                (city && city.toLowerCase().includes('toronto')) || // check city as well
                (state && state.toLowerCase().includes('ontario'))) {
                continue;
            }

            // This is the number of jobs in that city:
            const count = locations[i].city_count || locations[i]?.count || 0;
            // We'll also look for the jobIndexes set if it exists
            const jobIndexes = locations[i]?.jobIndexes || [];

            // Convert "Remote - USA" etc. to a consistent country & city name
            // The 'name' variable will be used for the actual filter option display name.
            if (name.toLowerCase().includes("remote - usa")) {
                country = "United States of America";
                name = "Remote"; // Ensures filter option is "Remote" for "Remote - USA" jobs
            } else if (name.includes("Remote - UK")) {
                country = "Europe";  // Change from "United Kingdom" to "Europe"
                name = "Remote - UK";
                // Add these jobs to Europe category
                if (!allLocs["Europe"]) {
                    allLocs["Europe"] = {
                        cities: [],
                        checked: false,
                        count: 0
                    };
                }
                // Add to Europe cities if not already present
                const existingRemoteUK = allLocs["Europe"].cities.find(city => city.name === "Remote - UK");
                if (!existingRemoteUK) {
                    allLocs["Europe"].cities.unshift({
                        city: city,
                        value: locations[i],
                        checked: false,
                        country: "Europe",
                        state,
                        name: name,
                        count: count || jobIndexes.length,
                        jobIndexes: jobIndexes,
                    });
                } else {
                    // Update existing Remote - UK entry with combined counts and job indexes
                    const newJobIndexes = [...new Set([...existingRemoteUK.jobIndexes, ...jobIndexes])];
                    existingRemoteUK.jobIndexes = newJobIndexes;
                    existingRemoteUK.count = newJobIndexes.length;
                }
                // Add to Europe's job set
                if (!countryToJobsMap["Europe"]) {
                    countryToJobsMap["Europe"] = new Set();
                }
                jobIndexes.forEach((jIdx) => countryToJobsMap["Europe"].add(jIdx));
                continue;
            }
            if (name.includes("Remote - Canada")) {
                country = "Canada";
                name = "Remote - Canada";
            }

            if (country === "") continue;

            // Check if this is a European country
            const isEuropean = europeanCountries.includes(country) || country === "Europe";

            // If European, add to Europe group
            if (isEuropean || country === "Europe") {
                // Add city to Europe object - maintain the same object signature
                // but set country to "Europe" and keep the original country in the name
                if (name.includes("Remote")) {
                    allLocs["Europe"].cities.unshift({
                        city: city,
                        value: locations[i],
                        checked: false,
                        country: "Europe", // Set country to Europe
                        state,
                        name: name, // Keep the original name
                        count,
                        jobIndexes,
                    });
                } else {
                    allLocs["Europe"].cities.push({
                        city: city,
                        value: locations[i],
                        checked: false,
                        country: "Europe", // Set country to Europe
                        state,
                        name: name, // Keep the original name
                        count,
                        jobIndexes,
                    });
                }

                // Build or append to the set for Europe
                if (!countryToJobsMap["Europe"]) {
                    countryToJobsMap["Europe"] = new Set();
                }

                // After processing all locations, remove any top-level "Remote - Europe" entry if it exists
                // and add it to the Europe object's cities array
                if (allLocs["Remote - Europe"]) {
                    const remoteEuropeData = allLocs["Remote - Europe"];
                    delete allLocs["Remote - Europe"];
                    allLocs["Europe"].cities.unshift({
                        city: "Remote",
                        value: "Remote - Europe",
                        checked: false,
                        country: "Europe",
                        state: "",
                        name: "Remote - Europe",
                        count: remoteEuropeData.count || 0,
                        jobIndexes: remoteEuropeData.jobIndexes || [],
                    });
                }
                // Add each job ID from the city to Europe's set
                jobIndexes.forEach((jIdx) => countryToJobsMap["Europe"].add(jIdx));
            } else {
                // Non-European country - handle normally
                // Initialize if new country
                if (!allLocs[country]) {
                    allLocs[country] = {
                        cities: [],
                        checked: false,
                        count: 0,
                    };
                }

                // Insert the city at front if it includes "Remote", for user convenience
                if (name.includes("Remote")) {
                    allLocs[country].cities.unshift({
                        city,
                        value: locations[i],
                        checked: false,
                        country,
                        state,
                        name,
                        count,
                        jobIndexes,
                    });
                } else {
                    allLocs[country].cities.push({
                        city,
                        value: locations[i],
                        checked: false,
                        country,
                        state,
                        name,
                        count,
                        jobIndexes,
                    });
                }

                // Build or append to the set for the country
                if (!countryToJobsMap[country]) {
                    countryToJobsMap[country] = new Set();
                }
                // Add each job ID from the city to the country's set
                jobIndexes.forEach((jIdx) => countryToJobsMap[country].add(jIdx));
            }
        }

        // Now compute final "country.count" from the size of each country's job set
        Object.keys(countryToJobsMap).forEach((country) => {
            if (allLocs[country]) {
                // Update the country's total count
                allLocs[country].count = countryToJobsMap[country].size;

                // Update each city's count based on its unique job indexes
                allLocs[country].cities.forEach(city => {
                    if (city.jobIndexes) {
                        // Ensure we're using unique job indexes for the count
                        const uniqueJobIndexes = [...new Set(city.jobIndexes)];
                        city.count = uniqueJobIndexes.length;
                        city.jobIndexes = uniqueJobIndexes; // Update to use unique indexes
                    }
                });
            }
        });

        console.log("allLocs")
        console.log(allLocs)
        // After processing all locations, ensure Remote - UK is properly set up
        const europeCategory = allLocs["Europe"];
        if (europeCategory) {
            const remoteUK = europeCategory.cities.find(city => city.name === "Remote - UK");

            if (!remoteUK) {
                // If Remote - UK doesn't exist, create it with minimum count
                europeCategory.cities.unshift({
                    city: "",
                    value: {
                        country: "Europe",
                        city: "",
                        name: "Remote - UK",
                        count: 0,
                        city_count: 0,
                        description: "Remote work opportunities in the UK",
                        state: "",
                        jobIndexes: []
                    },
                    checked: false,
                    country: "Europe",
                    state: "",
                    name: "Remote - UK",
                    count: 1,
                    jobIndexes: []
                });
            }

            // Update Europe's total count
            europeCategory.count = Math.max(1, countryToJobsMap["Europe"].size);
        }

        
        if (allLocs["Europe"]) {
            const remoteUK = allLocs["Europe"].cities.find(city => city.name === "Remote - UK");
            console.log(remoteUK)
            if (remoteUK) {
                remoteUK.count = Math.max(0, remoteUK.count);
                remoteUK.value.count = Math.max(0, remoteUK.count);
                remoteUK.value.city_count = Math.max(0, remoteUK.count);
            }
        }


        // Handle initial filter for locations
        if (initialFilter) {
            const parsed = initialFilter.split(':');
            if (parsed[0] === 'l' && parsed[1]) {
                // Decode the URL-encoded location name
                const cityName = decodeURIComponent(parsed[1]);
                const normalizedCityName = normalizeLocationName(cityName);

                console.log('Looking for location:', normalizedCityName);

                // Find matching location in the normalized locations
                let matchedCityObj = null;
                Object.keys(allLocs).forEach((country) => {
                    if (allLocs[country] && allLocs[country].cities) {
                        allLocs[country].cities.forEach((cityObj) => {
                            const normalizedCityObjName = normalizeLocationName(cityObj.name);
                            console.log('Comparing with:', normalizedCityObjName);

                            // More precise matching for remote locations
                            if (normalizedCityName.toLowerCase() === 'remote - canada') {
                                if (normalizedCityObjName.toLowerCase() === 'remote - canada') {
                                    console.log('Found Remote - Canada match');
                                    matchedCityObj = cityObj;
                                }
                            } else if (normalizedCityName.toLowerCase() === 'remote - uk') {
                                if (normalizedCityObjName.toLowerCase() === 'remote - uk') {
                                    console.log('Found Remote - UK match');
                                    matchedCityObj = cityObj;
                                }
                            } else if (normalizedCityName.toLowerCase() === 'remote') {
                                if (normalizedCityObjName.toLowerCase() === 'remote') {
                                    console.log('Found Remote match');
                                    matchedCityObj = cityObj;
                                }
                            } else {
                                // For non-remote locations, use exact matching
                                if (normalizedCityObjName &&
                                    normalizedCityObjName.toLowerCase() === normalizedCityName.toLowerCase()) {
                                    console.log('Found match:', cityObj.name);
                                    matchedCityObj = cityObj;
                                }
                            }
                        });
                    }
                });

                // If we found a match, update the filters
                if (matchedCityObj) {
                    console.log('Applying filter for:', matchedCityObj.name);

                    // First reset all locations
                    Object.keys(allLocs).forEach((loc) => {
                        if (allLocs[loc]) {  // Add null check
                            allLocs[loc].checked = false;
                            if (allLocs[loc].cities) {
                                allLocs[loc].cities.forEach((city) => {
                                    if (city) {  // Add null check
                                        city.checked = false;
                                    }
                                });
                            }
                        }
                    });

                    // Find and check the parent country if it exists
                    if (matchedCityObj.country && allLocs[matchedCityObj.country]) {
                        allLocs[matchedCityObj.country].checked = true;
                    }

                    // Set the matched city as checked
                    matchedCityObj.checked = true;

                    // Update the cities array in the parent country
                    if (matchedCityObj.country && allLocs[matchedCityObj.country]) {
                        const country = allLocs[matchedCityObj.country];
                        if (country.cities) {
                            country.cities = country.cities.map(city => {
                                if (city && city.name === matchedCityObj.name) {
                                    return { ...city, checked: true };
                                }
                                return city;
                            });
                        }
                    }

                    // Update the locations state with the matched city name
                    const result = updateLocationHelper(matchedCityObj, allLocs, []);
                    setAllLocationOptions(result[0]);
                    selectLocations([matchedCityObj.name]);
                } else {
                    console.log('No match found for:', normalizedCityName);
                    // If no match found, default to "All Locations"
                    Object.keys(allLocs).forEach((loc) => {
                        if (allLocs[loc]) {  // Add null check
                            allLocs[loc].checked = false;
                            if (allLocs[loc].cities) {
                                allLocs[loc].cities.forEach((city) => {
                                    if (city) {  // Add null check
                                        city.checked = false;
                                    }
                                });
                            }
                        }
                    });
                    if (allLocs["All Locations"]) {  // Add null check
                        allLocs["All Locations"].checked = true;
                    }
                    setAllLocationOptions(allLocs);
                    selectLocations(["All Locations"]);
                }
            }
        } else {
            // Default to "All Locations" checked
            if (allLocs["All Locations"]) {  // Add null check
                allLocs["All Locations"].checked = true;
            }
        }

        console.log('Normalized locations:', allLocs);
        setAllLocationOptions(allLocs);
    };

    const normalizeCapabilities = (capabilities) => {
        const allCaps = {};
        // Initialize "All Careers" first
        allCaps["All Careers"] = {
            checked: false,
            count: 0
        };

        // Sort capabilities
        const sortedCaps = capabilities.sort((a, b) => {
            if (a.name === b.name) return 0;
            if (a.name === 'All Careers') return -1;
            if (b.name === 'All Careers') return 1;
            return a.name < b.name ? -1 : 1;
        });

        // Initialize all capabilities
        sortedCaps.forEach((cap) => {
            if (cap && cap.name) {  // Add null check
                allCaps[cap.name] = {
                    checked: false,
                    count: cap.count || 0,
                };
            }
        });

        // Handle initial filter for capabilities
        if (initialFilter) {
            const parsed = initialFilter.split(':');
            if (parsed[0] === 'c' && parsed[1]) {
                const capabilityName = decodeURIComponent(parsed[1]);
                console.log('Looking for capability:', capabilityName);

                // Find matching capability
                let matchedCapability = null;
                Object.keys(allCaps).forEach((cap) => {
                    if (cap.toLowerCase() === capabilityName.toLowerCase()) {
                        console.log('Found capability match:', cap);
                        matchedCapability = cap;
                    }
                });

                // If we found a match, update the filters
                if (matchedCapability) {
                    console.log('Applying capability filter for:', matchedCapability);

                    // First reset all capabilities
                    Object.keys(allCaps).forEach((cap) => {
                        allCaps[cap].checked = false;
                    });

                    // Set the matched capability as checked
                    allCaps[matchedCapability].checked = true;

                    // Update the capabilities state
                    setAllCapabilityOptions(allCaps);
                    selectCapabilities([matchedCapability]);
                } else {
                    console.log('No capability match found for:', capabilityName);
                    // If no match found, default to "All Careers"
                    Object.keys(allCaps).forEach((cap) => {
                        allCaps[cap].checked = false;
                    });
                    allCaps["All Careers"].checked = true;
                    setAllCapabilityOptions(allCaps);
                    selectCapabilities(["All Careers"]);
                }
            } else {
                // Default to "All Careers" checked
                allCaps["All Careers"].checked = true;
            }
        } else {
            // Default to "All Careers" checked
            allCaps["All Careers"].checked = true;
        }

        console.log('Normalized capabilities:', allCaps);
        setAllCapabilityOptions(allCaps);
    };

    /************************************************
     * FETCH + PARSE JOB DATA
     ***********************************************/
    React.useEffect(() => {
        if (DynamicContent) {
            fetch(r3cPageContent + '?route=/job-category')
                .then((response) => response.json())
                .then((pgData) => {
                    if (!pgData.locations) {
                        pgData.locations = [];
                    }

                    // Clean up jobs
                    for (let i = 0; i < pgData.jobs.length; i++) {
                        // standardize "Remote - USA" => "Remote"
                        if (pgData.jobs[i].primaryLocation === "Remote - USA") {
                            pgData.jobs[i].primaryLocation = "Remote";
                        }
                        // Remove incomplete or null-locations
                        if (
                            !pgData.jobs[i].primaryLocation ||
                            pgData.jobs[i].primaryLocation === "Remote - CT"
                        ) {
                            pgData.jobs.splice(i, 1);
                            i--;
                            continue;
                        }
                        // Split the "locations" field if present
                        if (pgData.jobs[i].locations) {
                            pgData.jobs[i].locations = pgData.jobs[i].locations.split(';');
                        }
                    }

                    setAllJobs(pgData.jobs);
                    // For search prototype
                    setTotalJobs(pgData.jobs);

                    /**
                     * 1) Build a dictionary { cityName -> Set of jobIndexes }
                     *    So city_count = that set's size.
                     * 2) Then attach city_count to each "location" object in `pgData.jobLocations`.
                     */
                    let cityJobsMap = {};
                    // We'll also keep track of [jobIndex -> locationName[]] or direct parsing
                    pgData.jobs.forEach((job, idx) => {
                        // Each job can have multiple distinct locations
                        let locs = job.locations || [job.primaryLocation];
                        locs.forEach((loc) => {
                            const normalizedLoc = normalizeLocationName(loc.trim());
                            if (!cityJobsMap[normalizedLoc]) {
                                cityJobsMap[normalizedLoc] = new Set();
                            }
                            cityJobsMap[normalizedLoc].add(idx);
                        });
                    });

                    // Build an array with city + city_count
                    let locationArray = Object.entries(cityJobsMap).map(([name, jobSet]) => {
                        return {
                            name,
                            count: jobSet.size,
                            // keep track of which jobs are in this city for further country-level use
                            jobIndexes: [...jobSet],
                        };
                    });

                    // First, let's find all Remote - UK jobs
                    const remoteUKJobIndexes = new Set();
                    pgData.jobs.forEach((job, idx) => {
                        // Check primary location
                        if (job.primaryLocation && 
                            normalizeLocationName(job.primaryLocation).toLowerCase() === 'remote - uk') {
                            remoteUKJobIndexes.add(idx);
                        }
                        // Check locations array
                        if (job.locations) {
                            job.locations.forEach(loc => {
                                if (normalizeLocationName(loc).toLowerCase() === 'remote - uk') {
                                    remoteUKJobIndexes.add(idx);
                                }
                            });
                        }
                    });

                    // Next, we update pgData.jobLocations by matching them to locationArray
                    // so that each location item has city_count + jobIndexes
                    let newArray = pgData.jobLocations.map((location) => {
                        const normalizedName = normalizeLocationName(location.name);
                        
                        // Normal handling for locations
                        const updatedCity = locationArray.find(
                            (locObj) => normalizeLocationName(locObj.name) === normalizedName
                        );
                        if (updatedCity) {
                            return {
                                ...location,
                                city_count: updatedCity.count,
                                jobIndexes: updatedCity.jobIndexes || [],
                            };
                        }
                        return {
                            ...location,
                            city_count: 0,
                            jobIndexes: [],
                        };
                    });

                    // Add Remote - UK entry if we found any Remote - UK jobs
                    if (remoteUKJobIndexes.size > 0) {
                        newArray.push({
                            name: "Remote - UK",
                            country: "Europe",
                            city: "",
                            state: "",
                            description: "Remote work opportunities in the UK",
                            city_count: remoteUKJobIndexes.size,
                            jobIndexes: [...remoteUKJobIndexes],
                            count: remoteUKJobIndexes.size
                        });
                    }

                    // We remove "Creative" from capabilities if needed
                    let idxCreative = pgData.capabilities?.findIndex((x) => x.name === "Creative");
                    if (idxCreative > -1) {
                        pgData.capabilities.splice(idxCreative, 1);
                    }

                    // Combine capabilities + filters
                    let allCaps = [...pgData.capabilities, ...pgData.filters];

                    console.log({newArray})
                    // Handle locations and capabilities separately
                    normalizeLocations(newArray);
                    normalizeCapabilities(allCaps);

                    setLoading(false);
                });
        } else {
            // If DynamicContent is not available, just load a dummy array
            setAllJobs([
                {
                    detailLink: "/job-detail",
                    date: "Posted: January 1, 2020",
                    primaryLocation: "London",
                    department: "Integrated Communications",
                    title: "Senior Account Manager",
                },
            ]);
        }
    }, [initialFilter]);

    /************************************************
     * UPDATE CURRENT JOBS EVERY TIME FILTERS CHANGE
     ***********************************************/
    React.useEffect(() => {
        // Re-scroll the job‐results container to top
        let matchedJobs = [];

        // For every chosen capability + every chosen location,
        // gather matching jobs
        for (let capIdx = 0; capIdx < capabilities.length; capIdx++) {
            let capability = capabilities[capIdx];
            for (let locIdx = 0; locIdx < locations.length; locIdx++) {
                let location = locations[locIdx];
                for (let j = 0; j < allJobs.length; j++) {
                    const job = allJobs[j];
                    const isAllLocations = location === 'All Locations';
                    const isAllCareers = capability === 'All Careers';

                    // Normalize job locations
                    const normalizedJobLocation = (loc) => {
                        if (!loc) return '';
                        const lowerLoc = loc.toLowerCase();
                        if (lowerLoc.includes('toronto') ||
                            (job.state && job.state.toLowerCase().includes('ontario'))) {
                            return 'Remote - Canada';
                        }
                        return normalizeLocationName(loc);
                    };

                    // More precise location matching
                    const inLocation = (() => {
                        if (isAllLocations) return true;

                        const normalizedLocation = normalizeLocationName(location).toLowerCase();
                        const normalizedPrimaryLocation = normalizedJobLocation(job.primaryLocation).toLowerCase();
                        const normalizedJobLocations = (job.locations || []).map(loc => normalizedJobLocation(loc).toLowerCase());

                        // Special case for Remote - Canada
                        if (normalizedLocation === 'remote - canada') {
                            return normalizedPrimaryLocation === 'remote - canada' ||
                                normalizedJobLocations.includes('remote - canada');
                        }

                        // Special case for Remote - UK
                        if (normalizedLocation === 'remote - uk') {
                            return normalizedPrimaryLocation === 'remote - uk' ||
                                normalizedJobLocations.includes('remote - uk');
                        }

                        // Special case for Remote (USA)
                        if (normalizedLocation === 'remote') {
                            return normalizedPrimaryLocation === 'remote' ||
                                normalizedJobLocations.includes('remote');
                        }

                        // Special case for Switzerland/Zurich
                        if (normalizedLocation === 'zurich, switzerland') {
                            return normalizedPrimaryLocation === 'switzerland' ||
                                normalizedJobLocations.includes('switzerland');
                        }

                        // For all other locations, use exact matching
                        return normalizedPrimaryLocation === normalizedLocation ||
                            normalizedJobLocations.includes(normalizedLocation);
                    })();

                    // More precise capability matching
                    const inCapability = (() => {
                        if (isAllCareers) return true;

                        // Check both bucket and filterBucket for exact matches
                        return job.bucket === capability ||
                            job.filterBucket === capability ||
                            // Also check if the capability is part of the job's department
                            (job.department && job.department.toLowerCase() === capability.toLowerCase());
                    })();

                    if (inLocation && inCapability && !matchedJobs.includes(job)) {
                        // Update the job's location to show as Remote - Canada if it's Toronto
                        if (job.primaryLocation.toLowerCase().includes('toronto') ||
                            (job.state && job.state.toLowerCase().includes('ontario'))) {
                            job.primaryLocation = 'Remote - Canada';
                        }
                        if (job.locations) {
                            job.locations = job.locations.map(loc =>
                                loc.toLowerCase().includes('toronto') ||
                                    (job.state && job.state.toLowerCase().includes('ontario'))
                                    ? 'Remote - Canada'
                                    : loc
                            );
                        }
                        matchedJobs.push(job);
                    }
                }
            }
        }

        setCurrentJobs(matchedJobs);
    }, [allJobs, capabilities, locations]);

    /************************************************
     * SEARCH INPUT HANDLERS
     ***********************************************/
    const handleSearch = (e) => {
        let searchedTerm = e.target.value.toLowerCase();
        setSearchedJob(searchedTerm);

        let result = [];
        if (searchedTerm.length > 2) {
            result = totalJobs.filter((job) => {
                if (job.title?.toLowerCase().includes(searchedTerm)) return true;
                if (job.department?.toLowerCase().includes(searchedTerm)) return true;
                if (job.primaryLocation?.toLowerCase().includes(searchedTerm)) return true;
                if (job.departmentDescription?.toLowerCase().includes(searchedTerm)) return true;
                return false;
            });
        } else {
            result = totalJobs;
        }
        setAllJobs(result);
    };

    const scrollToJobResults = () => {
        document.activeElement.blur();
        let jobResults = document.getElementById('job-results');
        let position = jobResults.getBoundingClientRect().top;
        let offset = 71;
        window.scrollTo({
            left: 0,
            top: position - offset,
            behavior: 'smooth',
        });
    };

    const handleSearchClick = () => {
        if (window.innerWidth < 992) {
            scrollToJobResults();
        } else {
            // just focus the input
            document.getElementById('search').focus();
        }
    };

    const handleKeyDown = (e) => {
        if (window.innerWidth < 992 && e.key === 'Enter') {
            scrollToJobResults();
        }
    };

    /************************************************
     * UPDATE SELECTED (UI) LOCATIONS/CAPABILITIES
     ***********************************************/
    const updateSelectedCity = (option) => {
        const [locsObj, locsArr] = updateLocationHelper(option, allLocations, locations);
        setAllLocationOptions(locsObj);
        selectLocations(locsArr);
    };

    const updateSelectedCountry = (option) => {
        const [locsObj, locsArr] = updateLocationHelper(option, allLocations, locations);
        setAllLocationOptions(locsObj);
        selectLocations(locsArr);
    };

    const updateSelectedCapability = (option) => {
        const [capsObj, capsArr] = updateCapabilityHelper(option, allCapabilities, capabilities);
        setAllCapabilityOptions(capsObj);
        selectCapabilities(capsArr);
    };


    return (
        <JobCategoryPageWrap className="pageWrapper">
            <WideScreenContainer>
                <HeaderContainer>
                    {/* GPTW certification */}
                    <BadgeContainer>
                        <a
                            href="http://www.greatplacetowork.com/certified-company/7038366"
                            title="Rating and Review"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img src="/images/Real_Chemistry_US_English_2025_Certification_Badge.svg" alt="Review" width="100" />
                        </a>
                    </BadgeContainer>

                    <TitleContainer padding="1.5em 6.5em .5em">
                        <TitleXl textAlign="center">
                            All job opportunities<span className="magenta">.</span>
                        </TitleXl>
                        <p className="title-copy">
                            Not seeing an open role that's right for you? Join our talent community
                            and we'll keep you informed of career opportunities and events matching
                            your interests.
                        </p>
                    </TitleContainer>

                    <ViewAllContainer>
                        <ViewAll
                            target="_blank"
                            link={
                                'https://w2o.wd1.myworkdayjobs.com/en-US/RealChemistry/introduceYourself'
                            }
                            title={'STAY CONNECTED'}
                            borderColor="#FFF"
                            titleColor="#FFF"
                            hoverBackground="#FFF"
                            width="275px"
                            height="38px"
                            svgColor="#FFF"
                        />
                    </ViewAllContainer>

                    <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                        <BodySm
                            className="intro-text"
                            style={{ margin: '20px 0', color: 'white', fontSize: '20px' }}
                        >
                            <Link to={"/scam-alert"}>
                                <span style={{ color: '#ADD8E6' }}>Read more </span>
                            </Link>
                            about our commitment to safe online recruiting.
                        </BodySm>
                    </div>

                    {/* Search Bar */}
                    <SearchGroup>
                        <SearchBox>
                            <SearchBtn
                                onClick={handleSearchClick}
                                backgroundImage={'/images/search-icon-white.svg'}
                                alt="white magnifying glass icon"
                                aria-label="Search button for job listings on this site"
                                type="button"
                                name="Search button"
                            />
                            <SearchInput
                                id="search"
                                type="search"
                                aria-label="Search for job listings on this site"
                                placeholder="Search by role or by keyword"
                                name="job-search"
                                min="3"
                                value={searchedJob}
                                onChange={handleSearch}
                                onKeyDown={handleKeyDown}
                            />
                        </SearchBox>
                    </SearchGroup>

                    <Link to={"/careers"}>
                        <BackArrow className="cta arrow back">
                            <BackArrowSvg />
                            <TitleXs textTransform={"uppercase"} margin={"0 0 0 15px"}>
                                BACK TO CAREERS
                            </TitleXs>
                        </BackArrow>
                    </Link>
                </HeaderContainer>

                <Row className="job-filter-wrap">
                    {/* Left Column: Filters */}
                    <Col5 className="job-search-section">
                        <CategoryFilterSection ref={filterRef}>
                            <TitleLg>FILTER BY</TitleLg>

                            <ResetButton
                                type="button"
                                className={
                                    searchedJob.length > 2 || currentJobs.length < allJobs.length
                                        ? "searched"
                                        : ""
                                }
                                style={{ textTransform: "uppercase" }}
                                onClick={clearFilters}
                            >
                                Clear filters
                            </ResetButton>

                            <CollapsibleCheckboxArea
                                type="Career"
                                handleDropdownSelect={updateSelectedCapability}
                                title="Career Focus"
                                options={allCapabilities}
                            />

                            <CollapsibleCheckboxArea
                                type="Location"
                                handleOnInputChange={updateSelectedCity}
                                handleDropdownSelect={updateSelectedCountry}
                                title="Location"
                                options={allLocations}
                            />
                        </CategoryFilterSection>
                    </Col5>

                    {/* Right Column: Job Listings */}
                    <Col7
                        ref={locationRef}
                        className="job-right"
                        style={{ height: filterRef.current?.clientHeight }}
                    >
                        <FilterCard scrollTop={scrollTop}>
                            <BodySm color="#fff" textTransform="uppercase">
                                {locations.join(', ')}
                            </BodySm>
                            <BodySm color="#fff" textTransform="uppercase">
                                {capabilities.join(', ')}
                            </BodySm>
                        </FilterCard>

                        <BadgesRow
                            id="job-results"
                            className={
                                searchedJob.length > 2 || currentJobs.length < allJobs.length
                                    ? "searched"
                                    : ""
                            }
                        >
                            {searchedJob.length > 2 && (
                                <BodySm>Searched for: "{searchedJob}"</BodySm>
                            )}
                            <TitleSm style={{ textTransform: "uppercase", fontWeight: "400" }}>
                                <span
                                    style={{
                                        fontWeight: "900",
                                        paddingRight: "5px",
                                    }}
                                >
                                    {currentJobs.length > 0 ? currentJobs.length : ''}
                                </span>
                                {currentJobs.length !== 0 && searchedJob.length < 3
                                    ? 'Jobs Available'
                                    : ''}
                                {searchedJob.length > 2 && currentJobs.length !== 0
                                    ? ' Job Search Results'
                                    : ''}
                                {currentJobs.length === 0 ? 'No Results Found' : ''}
                            </TitleSm>
                        </BadgesRow>

                        <CategorySectionRight className="listings">
                            {currentJobs.map((item, index) => {

                                const formatLocation = (location) => {
                                    if (!location || typeof location !== 'string') return '';

                                    // Handle special 'Remote' patterns
                                    if (/remote/i.test(location)) {
                                        const match = location.match(/Remote(?:\s*-\s*(.+))?/i);
                                        const country = match && match[1] ? match[1].trim() : '';
                                        return country ? `Remote - ${country}` : 'Remote';
                                    }

                                    // Handle 'City, Country' or 'Country, City'
                                    const parts = location.split(',').map(p => p.trim());


                                    if (parts.length === 2) {
                                        const [part1, part2] = parts;
                                        return part1.length >= part2.length ? `${part1}, ${part2}` : `${part2}, ${part1}`;
                                    }

                                    // If not matching, return as is
                                    return location;
                                };

                                /**
                                 * Helper function to construct a unique, pipe-separated jobLocation string
                                 * Filters out duplicates and ensures "Remote" appears only once.
                                 */
                                const constructJobLocation = (department, primaryLocation, locations) => {
                                    // Helper function to check if a string includes 'remote' (case-insensitive)
                                    const includesRemote = (str) => {
                                        return typeof str === 'string' && str.toLowerCase().includes('remote');
                                    };

                                    // Initialize an array with department and primaryLocation
                                    let jobLocationArray = [department, primaryLocation];

                                    // If locations exist, add them to the array
                                    if (locations && locations.length > 0) {
                                        jobLocationArray = jobLocationArray.concat(locations);
                                    }

                                    // Normalize the array for duplicate removal:
                                    // - Convert to lowercase for comparison
                                    // - Use a Set to track seen locations
                                    const seen = new Set();
                                    const filteredJobLocation = [];

                                    jobLocationArray.forEach((loc) => {
                                        if (typeof loc !== 'string') return; // Skip non-string entries

                                        const formattedLoc = formatLocation(loc);
                                        const normalizedLoc = formattedLoc.trim().toLowerCase();


                                        // Special handling for "Remote"
                                        if (includesRemote(loc)) {
                                            if (!seen.has('remote')) {
                                                filteredJobLocation.push('Remote'); // Add with proper casing
                                                seen.add('remote');
                                            }
                                            // Else, already added "Remote", skip duplicates
                                        } else {
                                            if (!seen.has(normalizedLoc)) {
                                                filteredJobLocation.push(normalizedLoc.trim());
                                                seen.add(normalizedLoc);
                                            }
                                            // Else, duplicate location, skip
                                        }
                                    });

                                    // Join the filtered array with ' | '
                                    return filteredJobLocation.join(' | ');
                                };

                                // Construct the jobLocation string using the helper function
                                const jobLocation = constructJobLocation(
                                    item.department,
                                    item.primaryLocation,
                                    item.locations
                                );

                                return (
                                    <JobPostCard
                                        borderLeft="none"
                                        jobLink={item.detailLink}
                                        jobLocation={jobLocation}
                                        jobTitle={item.title}
                                        key={`jobPostCard-${index}`} // Consider using a unique ID if available
                                        postDate={item.date}
                                    />
                                );
                            })}
                        </CategorySectionRight>

                    </Col7>
                </Row>
            </WideScreenContainer>

            <FooterAnimation
                svgDesktop="/images/purple-footer.svg"
                svgMobile="/images/purple-footer-mbl.svg"
                borderTop="none"
            />
        </JobCategoryPageWrap>
    );
}

export default LoadingHoc(JobCategoryPage);
